import type { Cadence, SchedulableAuditType } from './cadence';
import type { IndustryCode } from './industry';

export type LocationScope = 'worldwide' | 'national' | 'city' | 'address';

/**
 * First-class tracked-competitor relationship row (Competitors feature, plan §2.1).
 * Stored on the brand-queries table: PK=`BRAND#<brandId>`, SK=`COMPETITOR#<domain>`.
 * `domain` is the dedup key shared with the (future) DomainProfile cache. `brand.competitors`
 * (names array) stays in sync as the agent-payload source of truth; these rows add the
 * domain + pinned flag + a zero-migration path to a reverse GSI later.
 */
export interface TrackedCompetitor {
  brandId: string;
  /** Normalized: lowercased, no scheme/path, no leading www. */
  domain: string;
  /** Display name (what appears in `brand.competitors` and matrix columns). */
  name: string;
  /** Pinned competitors always render in the matrix regardless of the top-N cap. */
  pinned?: boolean;
  addedAt: string;
}

/** One Class-B event in a competitor's evolution timeline (plan §2.3). */
export interface CompetitorEvent {
  kind: 'news' | 'website' | 'blog' | 'score' | 'cocite' | 'source' | string;
  title: string;
  detail?: string;
  sourceUrl?: string;
  /** Homepage-diff percentage — `website` events only. */
  diffPct?: number;
  createdAt: string;
}

/** Shared DomainProfile detail (the Class-B side of a competitor). */
export interface CompetitorDomainProfile {
  domain: string;
  name: string;
  /** Reverse-GEO inference (differentiators/targetQueries/contentPillars), when available. */
  profile?: Record<string, unknown> | null;
  lastFetchedAt?: string | null;
  refCount: number;
  /** `ok` = profile enriched; `failed` = scrape/parse failed (site down, parked,
   *  JS-only, 403); `undefined` = never attempted / in progress. */
  profileStatus?: 'ok' | 'failed';
}

/** Response of `GET /brands/:brandId/competitors/:domain/detail`. */
export interface CompetitorDetailResponse {
  profile: CompetitorDomainProfile | null;
  events: CompetitorEvent[];
}

export interface Location {
  locationId: string;
  label: string;
  zone: string;
  scope?: LocationScope;
  /** ISO 3166-1 alpha-2 (lowercase) for the zone's country — set by the
   *  zone-inference LLM at onboarding so the dashboard renders a flag without
   *  re-classifying at read time. Absent on older locations (dashboard falls
   *  back to a deterministic label→country dict in geoFlags.ts). */
  countryCode?: string;
  /** Geographic scale of the zone (village/ville/region/pays/europe/monde) —
   *  the same tag onboarding zone-inference already produces, now persisted. */
  scale?: string;
}

/**
 * Envergure = current geographic footprint / reach, not ambition or age.
 * - hyper-local : one address, neighborhood, or city (neighborhood baker, local artisan)
 * - multi-local : several cities within one region/country (regional franchise, multi-site SMB)
 * - mono-country : full-country presence or notoriety (national retailer, national SaaS)
 * - multi-country : presence across multiple countries (EU-wide B2B, DACH SaaS)
 * - global : worldwide presence without geographic constraint (Accor, BNP, scale-up)
 */
export type Envergure =
  | 'hyper-local'
  | 'multi-local'
  | 'mono-country'
  | 'multi-country'
  | 'global';

/** @deprecated Use Envergure instead. Kept for backward-compat during migration. */
export type BrandMaturity = 'emerging' | 'established' | 'leader';

export interface BrandExistingAssets {
  wikipediaUrl?: string;
  pressKitUrl?: string;
  notableMentions?: string[];
}

/**
 * Audience distribution — how the brand's prospects search, as a funnel from
 * unaware to deliberating. Captured in onboarding (use-case substep) and used
 * at query-generation time to bias prompt PHRASING (mostly referral + authority;
 * narrative is brand-name-anchored and ignores it). Values are percentages that
 * sum to 100 (the UI enforces the invariant via a constrained slider). Optional —
 * absent means "no phrasing bias" (today's behavior).
 */
export interface AudienceMix {
  /** Describes a pain without knowing the product category exists. Highest-funnel. */
  problemAware: number;
  /** Casual, non-technical. Wants a quick recommendation / unique name. */
  recommendation: number;
  /** Knows the product, asks about features and specs. */
  informed: number;
  /** Weighs tradeoffs, explores a decision space across options. */
  evaluative: number;
}

export type AudienceArchetype = keyof AudienceMix;

/**
 * Objective → axis mix. The marketing objective pre-fills a distribution over the
 * three Brand Perception axes; the user can edit it. Used at generation time to
 * split the shared prompt budget (tier `prompts` gauge for self-serve, or the
 * agency-requested total) across axes. Values sum to 100.
 */
export interface ObjectiveMix {
  referral: number;
  authority: number;
  narrative: number;
}

export type BrandObjective = 'leads' | 'ecosystem' | 'image';

/** Objective → default axis weights (pre-fill; editable afterwards). */
export const OBJECTIVE_PRESETS: Record<BrandObjective, ObjectiveMix> = {
  leads: { referral: 60, authority: 20, narrative: 20 },
  ecosystem: { referral: 20, authority: 60, narrative: 20 },
  image: { referral: 20, authority: 20, narrative: 60 },
} as const;

export interface BrandStrategy {
  aliases?: string[];
  /**
   * Standardized industry (fixed enum) — drives the cross-tenant authority-cache key
   * `(country, industryCode)` shared by Earned Media + News Watch. Set via a dropdown at
   * brand creation. Was free-text ≤80 chars pre-2026-06; migrated brands keep the old
   * string in `industryLegacy` and the mapped code here.
   */
  industry?: IndustryCode;
  /** @deprecated Original free-text industry, preserved on migration for audit/rollback. */
  industryLegacy?: string;
  clientPersonas?: string;
  businessModel?: 'b2b' | 'b2c' | 'both';
  geographicFocus?: string;
  description?: string;
  differentiators?: string[];
  scenarios?: string[];
  keywords?: string[];
  // PR#1 additions — optional, consumed progressively by agents
  customerQuestions?: string[];
  customerObjections?: string[];
  avoidTopics?: string[];
  targetLanguages?: string[];
  /**
   * Brand working language — the language in which ALL AI-generated analytical
   * prose is produced (Brand Perception synthesis/recommendations, Earned Media,
   * Content Quality, News Watch). Distinct from `targetLanguages`, which is the
   * measurement dimension (which languages we probe AI engines in). Set ONCE at
   * onboarding (default = creating user's UI language) and IMMUTABLE afterwards —
   * `BrandsService.update` strips it. Only `fr` / `en` are offered today.
   * Absent on pre-2026-07 brands → agents fall back to French (prior behavior).
   */
  workingLanguage?: 'fr' | 'en';
  /** Current geographic footprint. Drives scoring calibration, not query generation. */
  envergure?: Envergure;
  /** @deprecated Replaced by `envergure`. Read-only for existing records. */
  brandMaturity?: BrandMaturity;
  existingAssets?: BrandExistingAssets;
  /** Audience phrasing distribution (sums to 100). Set in onboarding. */
  audienceMix?: AudienceMix;
  /** Objective → axis budget split (sums to 100). Set in onboarding before GEO generation. */
  objectiveMix?: ObjectiveMix;
  /**
   * Agency-only: total number of prompts to generate across all axes. For
   * self-serve tiers the total is the `prompts` entitlement; agency's gauge is
   * unlimited, so the operator enters an explicit budget here. Used only at
   * generation time to split via `objectiveMix`.
   */
  promptBudget?: number;
}

/**
 * Generation-only knobs on BrandStrategy that shape prompt creation but are NOT
 * part of the agent-facing strategy content. Excluded from the validation hash
 * so editing them never flips `strategyMeta`/`geoStrategyMeta` to `stale`.
 */
export const STRATEGY_GENERATION_KNOBS = ['audienceMix', 'objectiveMix', 'promptBudget'] as const;

/**
 * `aliases` only affects deterministic brand-name matching in the Brand
 * Perception scorer (`is_brand_cited` / `brand_is_source` — see scoring.py)
 * and query-generation forbidden-terms — it doesn't change agent-facing
 * strategy content or how agents reason about the brand. Excluded from the
 * validation hash so adding/editing an alias (e.g. via the "Non cité ?" quick
 * fix on a Brand Perception result) never flips `strategyMeta`/
 * `geoStrategyMeta` to `stale` and never requires re-validation.
 */
export const STRATEGY_HASH_EXCLUDED_FIELDS = [...STRATEGY_GENERATION_KNOBS, 'aliases'] as const;

/**
 * Character limits enforced on both frontend (live counter) and backend (validation).
 * Keeps tenant storage predictable and prevents abuse.
 */
export const BRAND_STRATEGY_LIMITS = {
  industry: 80,
  clientPersonas: 500,
  geographicFocus: 200,
  description: 800,
  aliases: { maxItems: 10, maxCharsPerItem: 80 },
  differentiators: { maxItems: 10, maxCharsPerItem: 150 },
  scenarios: { maxItems: 15, maxCharsPerItem: 200 },
  keywords: { maxItems: 30, maxCharsPerItem: 100 },
  customerQuestions: { maxItems: 20, maxCharsPerItem: 200 },
  customerObjections: { maxItems: 20, maxCharsPerItem: 200 },
  avoidTopics: { maxItems: 20, maxCharsPerItem: 150 },
  targetLanguages: { maxItems: 10, maxCharsPerItem: 10 },
  existingAssets: {
    wikipediaUrl: 500,
    pressKitUrl: 500,
    notableMentions: { maxItems: 20, maxCharsPerItem: 300 },
  },
} as const;

export interface GuardrailViolation {
  field: string;
  message: string;
}

export interface CompetitorEarnedMediaSignal {
  sourceName: string;
  sourceUrl: string;
  snippet: string;
}

export interface CompetitorComparison {
  positioning: string;
  strengths: string[];
  weaknesses: string[];
  sharedTopics: string[];
  uniqueToCompetitor: string[];
}

export interface CompetitorAnalysis {
  name: string;
  url?: string;
  strategy?: Partial<BrandStrategy>;
  earnedMediaSignals: CompetitorEarnedMediaSignal[];
  comparison: CompetitorComparison;
  analyzedAt: string;
}

/**
 * Per-brand Website Audit configuration. Overrides the default Phase A triage behavior.
 *
 * - `includeUrls`: glob patterns — matching URLs bypass utility blocklist + category caps
 *   + budget truncation. Always kept (unless the page doesn't exist at crawl time).
 * - `excludeUrls`: glob patterns — matching URLs are hard-dropped before any other filter,
 *   regardless of category. Useful for "I never want you to audit /my-draft-page" cases.
 *
 * Globs support `*` (any-chars-within-segment), `**` (any-chars-across-segments), `?`
 * (single char). Example: `"**\/jobs/*"` excludes every job sub-page. Patterns are
 * matched against the full URL including protocol + host.
 */
/**
 * Materialized record of an EventBridge Scheduler schedule for one
 * (brand × axis) pair. Written by `ScheduleSyncService.syncBrand` (S3).
 *
 * This is a CACHE — the source of truth is `BrandAuditConfig.cadenceOverride`
 * + tier defaults via `resolveCadence()`. We store the materialized state
 * here so tenant purge + brand delete don't need a `ListSchedules` API call.
 */
export interface BrandSchedule {
  /** EventBridge Scheduler schedule name (deterministic — see ScheduleSyncService). */
  scheduleName: string;
  /** ARN — kept as the source of truth for `DeleteSchedule` calls. */
  scheduleArn: string;
  /** Last cadence written to EventBridge — kept as a cache so the API can compare without an SDK roundtrip. */
  cadence: Cadence;
  /** FlexibleTimeWindow value passed at create time, in minutes. */
  flexibleWindowMinutes: number;
  /** ISO timestamp of the create or last UpdateSchedule call. */
  createdAt: string;
  /**
   * ISO timestamp of the schedule's FIRST fire (EventBridge `StartDate`),
   * anchored one full cadence interval after creation so a freshly-onboarded
   * brand doesn't run the same axis twice on day one (onboarding manual run +
   * an immediate first cron fire). Preserved across re-syncs so cadence edits
   * don't keep pushing it out. Also the anchor for computing the next run date
   * locally (no AWS call) — see `nextRunAt` in cadence.ts.
   */
  firstRunAt?: string;
}

export interface BrandAuditConfig {
  includeUrls?: string[];
  excludeUrls?: string[];
  /**
   * Per-axis cadence overrides. Unset axes fall through to tier defaults.
   * `'off'` explicitly pauses an axis without falling through.
   */
  cadenceOverride?: Partial<Record<SchedulableAuditType, Cadence>>;
  /**
   * Source of truth for which EventBridge Scheduler schedules currently
   * exist. Written by `ScheduleSyncService.syncBrand` (S3). Never read
   * before S3 lands.
   */
  schedules?: Partial<Record<SchedulableAuditType, BrandSchedule>>;
  /**
   * Per-brand watchlist for the Earned Media orchestrator.
   *  - `include`: registered domains forced into every earned-media audit
   *    (Tavily runs an extra include_domains search per entry).
   *  - `exclude`: registered domains never logged for this brand
   *    (homonym filter — e.g. another company sharing the brand's name).
   * Max 20 entries per list, max 64 chars each, lowercased + deduped server-side.
   */
  earnedMediaWatch?: {
    include?: string[];
    exclude?: string[];
  };
  /**
   * Agency-only: per-brand AI engine allowlist, GLOBAL across all 3 Brand
   * Perception axes (narrative/authority/referral). Engine ids match the
   * `engines` allowlist registry (`perplexity` | `openai` | `gemini` | `claude`).
   * Intersected with the tier allowlist by the Trigger Lambda at dispatch, so it
   * only ever NARROWS the roster. Unset → full tier roster (no change to runs).
   * Drives the per-axis credit estimate (prompts × selected-engine-count).
   */
  enginesEnabled?: string[];
  /**
   * Agency-only: per-brand cap on how many pages the Website Audit crawls (1
   * credit / page analyzed). Scopes BOTH the agent's crawl budget (passed as
   * `pages_per_audit` in the SQS payload) AND the credit reserve. Unset → tier
   * default (agency = 100). Clamped server-side to 1..100 (the agent's hard
   * ceiling). The lever an agency uses to bound per-brand website-audit cost.
   */
  maxPagesPerWebsiteAudit?: number;
  /**
   * Agency-only: per-brand cap on how many third-party pages the Earned Media
   * audit fetches + judges (1 credit / fetch). Scopes BOTH the agent's fetch
   * budget (passed as `fetch_budget` in the SQS payload, overriding the
   * all-tiers `TIER_BUDGETS` default of 100) AND the credit reserve. Unset →
   * tier default (agency = 100). Clamped server-side to 1..100. The lever an
   * agency uses to bound per-brand earned-media cost.
   */
  maxFetchPerEarnedMedia?: number;
}

export interface Brand {
  brandId: string;
  tenantId: string;
  name: string;
  url?: string;
  /** Phase A triage overrides — optional per-brand include/exclude URL globs. */
  auditConfig?: BrandAuditConfig;
  /** Email digest config (S5). Per-recipient cadence, materialized to EventBridge Scheduler. */
  emailConfig?: import('./email').BrandEmailConfig;
  /**
   * CloudFront URL to the normalized 64x64 favicon PNG (at `tenants/<t>/brands/<b>/favicon-64.png`).
   * The 128x128 variant is the same path with `-64.png` → `-128.png`. Set lazily
   * by FaviconService — missing means "not yet fetched" and triggers a lazy backfill
   * call from the frontend.
   */
  faviconUrl?: string;
  /** ISO timestamp of the last successful favicon fetch. Used for cache control. */
  faviconFetchedAt?: string;
  locations: Location[];
  competitors: string[];
  strategy?: BrandStrategy;
  /**
   * Validation status + snapshot hash for `strategy`. Absent when no strategy has
   * ever been saved. See [geo-strategy.ts](./geo-strategy.ts) for the state machine.
   */
  strategyMeta?: import('./geo-strategy').BrandStrategyMeta;
  /**
   * Optional GEO action plan derived from `strategy`. Agents gate on the
   * `geoStrategyMeta.status === 'validated'` invariant at runtime.
   */
  geoStrategy?: import('./geo-strategy').GeoStrategy;
  geoStrategyMeta?: import('./geo-strategy').GeoStrategyMeta;
  competitorAnalyses?: Record<string, CompetitorAnalysis>;
  /**
   * ISO timestamp set ONCE when the brand-onboarding wizard is fully finished
   * (strategy + GEO validated, prompts created, first audits fired). Absent =
   * onboarding never completed → the app routes the user back into the wizard to
   * resume (a brand row exists from wizard step 1, long before finish).
   *
   * This is a one-way "did onboarding ever complete" marker. It is NOT cleared
   * when a strategy later goes `stale` — a validated-then-edited brand stays in
   * the app freely (resume-routing keys on this flag, never on `strategyMeta`).
   */
  onboardingCompletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Whether a brand has finished onboarding — used by the app's resume-routing
 * (a brand row exists from wizard step 1, long before the wizard finishes, so
 * "a brand exists" is NOT the same as "onboarding done").
 *
 * Primary signal: the write-once `onboardingCompletedAt` marker.
 *
 * Legacy fallback: brands created before the marker shipped have no
 * `onboardingCompletedAt`. Treat them as onboarded if their Brand Strategy was
 * ever validated (`validated` or `stale` — `stale` means "validated, then
 * edited", still a finished brand). This keeps existing customers OUT of the
 * wizard while still catching genuinely-abandoned half-brands (strategy never
 * past `draft`). New brands always get the explicit marker.
 */
export function isOnboardingComplete(
  brand: Pick<Brand, 'onboardingCompletedAt' | 'strategyMeta'>,
): boolean {
  if (brand.onboardingCompletedAt) return true;
  const s = brand.strategyMeta?.status;
  return s === 'validated' || s === 'stale';
}
